<template>
  <PopulationTabActions
    ref="populationTabActionsRef"
    :is-action-bar-disabled="isActionBarDisabled"
    :show-edit-button="showEditButton"
    :event-site="eventSite"
    search-placeholder="Search populations"
    :first-filter="populationTypeFilter"
    :initial-filter-keys="initialFilterKeys"
    :initial-filter-values="initialFilterValues"
    :include-overrides-filter="includeOverridesFilter"
    @search-applied="emit('search-applied', $event)"
    @filters-applied="emit('filters-applied', $event)"
    @edit-clicked="emit('edit-clicked')"
  />
</template>

<script setup lang="ts">
import { BittsIcon } from '@crossbeam/bitts/src/types';

import { computed, ref } from 'vue';

import { SEGMENTS_TO_LABEL_MAP, SegmentType } from '@/types/populations';

import PopulationTabActions from './population-tab/PopulationTabActions.vue';

type FilterValues = Record<string, (string | number)[]>;

const props = withDefaults(
  defineProps<{
    isActionBarDisabled?: boolean;
    showEditButton?: boolean;
    includeOverridesFilter?: boolean;
    initialOverridesFilter?: boolean;
    initialSegment?: SegmentType;
    /** Event site for dataSharingAction (e.g. default sharing populations, my data sharing). */
    eventSite?: string;
  }>(),
  {
    isActionBarDisabled: false,
    showEditButton: true,
    includeOverridesFilter: false,
    initialOverridesFilter: undefined,
    initialSegment: undefined,
    eventSite: undefined,
  },
);

const emit = defineEmits<{
  'search-applied': [value: string];
  'filters-applied': [filterValues: FilterValues];
  'edit-clicked': [];
}>();

const initialFilterKeys = computed(() => {
  const keys = ['population_type', 'sharing_level', 'field_presets'];
  if (props.includeOverridesFilter) keys.push('overrides');
  return keys;
});

const initialFilterValues = computed<FilterValues | undefined>(() => {
  const values: FilterValues = {};
  if (props.initialOverridesFilter !== undefined) {
    values.overrides = [String(props.initialOverridesFilter)];
  }
  if (props.initialSegment) values.population_type = [props.initialSegment];
  return Object.keys(values).length ? values : undefined;
});

const populationTypeFilter = computed(() => ({
  value: 'population_type',
  options: Object.entries(SEGMENTS_TO_LABEL_MAP).map(([value, label]) => ({
    label,
    value: value as SegmentType,
  })),
  label: 'Population Type',
  faIcon: ['fak', 'population'] as BittsIcon,
}));

const populationTabActionsRef = ref<InstanceType<
  typeof PopulationTabActions
> | null>(null);

function clearAllFilters() {
  populationTabActionsRef.value?.clearAllFilters();
}

defineExpose({
  clearAllFilters,
});
</script>
